import { type DeviceInfo } from '../requests';
import { type PollLaunchedRecord } from '../requests/polls';
import { type CamelCased } from '../utility/camelCase';
import { type SchoolSettingsRaw } from './schoolSettings';

type PinnedTab = {
    tab_type: 'screenshare' | 'classroom' | 'content-tab' | 'none';
    tab_id: number;
};

type ChatMessage = {
    data: {
        from: {
            initials: string;
            is_verified: boolean;
            name: string;
            participant_id: string;
            persistent_id: string;
            persistent_session_id: string;
            role: ClassRole;
            student_id?: number;
        };
        to?: {
            initials: string;
            is_verified: boolean;
            name: string;
            persistent_id: string;
            student_id?: number;
            role: ClassRole;
        };
        message_uuid: string;
        breakout_id?: string;
        content: string;
    };
    timestamp: number;
    from_student_id: string | null;
    from_persistent_id: string;
    from_persistent_session_id: string;
    to_persistent_session_id: string;
    to_student_id: string | null;
    class_session_chat_id: number;
    to_persistent_id: string | null;
    created_at: string;
    updated_at: string;
    is_hidden: boolean;
    type: 'private' | 'public' | 'instructors' | 'breakout';
};

type Class = {
    sid: number;
    school_name: string;
    class_name: string;
    class_code: string;
    courseid: string;
    data: ClassData;
    random_value: number;
    meeting_id: string;
    class_uuid: string;
    class_number: string;
    settings_data: SettingsDataRaw;
    date_created: Date;
    class_description: string;
    type: string;
    status: string;
    live_session: Session;
    active_session: Session;
    lti_state?: {
        url?: string;
    };
    lms_sync_settings?: {
        enable_content: boolean;
        content_learning_tools: 'lms-content' | 'instructor-choice';
    };
    content_type?: 'default' | 'lms';
};

type Session = {
    sid: number;
    class_id: string;
    start_time: string;
    end_time: string;
    weekday_str: string;
    date_str: Date;
    start_time_str: string;
    end_time_str: string;
    status: string;
    type: null;
    timezone: string;
    class_session_uuid: string;
    state_data: StateDataRaw;
    last_updated: Date;
    attendance_generated: null;
    metrics_generated: null;
    class_session_topic: null;
    class_uuid: string;
};

type ClassData = {
    class_description: string;
};

type CmsSettings = {
    id: number;
    url: string;
    name: string;
    type: string;
    class_id: number;
};

type ContentCount = {
    count: string;
    content_type: string;
};

type Syllabus = {
    class_content_id: number;
    class_id: number;
    content_name: string;
    content_type: string;
    timestamp: string;
    asset_id: string;
    content_data: ContentData;
};

type ContentData = {
    class_id: number;
    id: number;
    url: string;
    download_url: string;
    mime_type: string;
    uploaded: boolean;
    presentation_url?: string;
};

type Meeting = {
    meeting_id: string;
    meeting_number: string;
    topic: string;
    meeting_url: string;
    host_tag: null;
    meeting_password: string;
    is_imported: null;
    meeting_provider: string;
    external_meeting_id: string;
};

type Student = {
    email: string;
    firstName: string;
    lastName: string;
    random_value: number;
    role: ClassRole;
    sid: number;
    student_id: number;
    student_uuid: string;
    transparent_user_image: null;
    user_image: string;
    user_name: string;
};

type Tab = {
    class_id: string;
    class_session_uuid: string;
    close_timestamp: null;
    control_type: 'manual';
    data: TabData;
    dedupe_key: string;
    open_timestamp: number;
    sid: number;
    status: string;
    student_id: string;
    tab_id: number;
    tab_type: string;
    title: string;
};

type TabData = {
    breakout_name?: string;
    breakout_names?: string[];
    element?: string;
    uniqueId: string;
    url: string;
    data?: {
        breakout_names: string[];
    };
    cmid?: string | null;
    video_id?: string;
    control_type?: string;
};

type BreakoutSecurity = {
    disable_audio: boolean;
    disable_video: boolean;
    allowReturnToMain: boolean;
};

type BreakoutAttendee = {
    bid: string;
    breakout_id: string;
    display_name: string;
    breakout_user_id: string;
    zoom_id: number;
};

type BreakoutRoom = {
    boId: string;
    name: string;
    hostId: string;
    attendeeIdList: Array<string>;
    attendees: Array<BreakoutAttendee>;
    inviteList: Array<string>;
};

type BreakoutData = {
    rooms?: Array<BreakoutRoom>;
    controlStatus?: number;
    unassigned?: Array<string>;
};

type BreakoutNames = Array<string>;

type StateDataNonDemo = {
    is_demo: undefined;
};

type StateDataDemo = {
    is_demo: true;
    demo_config: {
        assistantCount: number;
        automated: boolean;
        camTypes: string[];
        newMeeting: boolean;
        role: ClassRole;
        studentCount: string;
        teacherCount: string;
        type: 'education' | 'corporate';
    };
    demo_users: Array<{
        id: number;
        isActive: boolean;
        absent: 'Yes' | 'No';
        audio: 'Yes' | 'No';
        extra: 'Yes' | 'No';
        feedback: FeedbackReaction;
        firstName: string;
        lastName: string;
        focused: 'Yes' | 'No';
        picture: string;
        presenting: 'Yes' | 'No';
        proctor_image: string;
        raisedHand: 'Yes' | 'No';
        role: 'Teacher' | 'Student' | 'TA' | 'class' | 'board' | 'sign_language';
        stars: string;
        video: 'Yes' | 'No';
        video_name: string;
        verified: 'Yes' | 'No';
    }>;
    demo_chats: {
        private_chats: {
            [fromUserName: string]: Array<{
                from?: string;
                to?: string;
                text: string;
            }>;
        };

        public_chats: Array<{
            from: string;
            text: string;
        }>;
    };
};

/** PUBLIC TYPES */

export type SettingsDataRaw = {
    auto_verify?: boolean;
    has_files: boolean;
    cms_settings?: CmsSettings;
    content_counts?: ContentCount[];
    share_setting_type: number;
    syllabus_content_id: number;
    selected_chat_option: ChatPermission;
    seating_chart_sync_setting: SeatingChart;
    syllabus?: Syllabus;
    persistent_spotlight_id?: number;
    pinned_under_instructor?: TemporaryRole;
    absent_demo_students?: {
        sid: number;
        role: 'student';
        first_name: string;
        last_name: string;
        user_name: string;
        student_id: number;
        student_uuid: number;
    }[];
};

export type StateDataRaw = (StateDataNonDemo | StateDataDemo) & {
    is_office_hours: boolean;
    proctor_mode: boolean;
    breakout_auto_assign_and_push: boolean;
    front_of_class: FrontOfClassType;
    privacy_mode: '' | PrivacyMode;
    breakout_security?: BreakoutSecurity;
    breakout_data: BreakoutData;
    breakout_names: BreakoutNames;
    screen_share_permissions?: ScreenSharePermissions;
    // For legacy Apps
    pinned_under_instructor?: TemporaryRole;

    restricted_chat_users?: {
        studentId?: number;
        classPersistentSessionId?: string;
    }[];
};

export enum ScreenSharePermissions {
    Locked = 'locked',
    InstructorSteal = 'instructor_steal',
    Unlocked = 'unlocked'
}

export enum ClassRole {
    Teacher = 'teacher',
    Assistant = 'assistant',
    Student = 'student'
}

export enum TemporaryRole {
    None = '',
    InstructorCam = 'board',
    ClassCam = 'class',
    SignLanguageCam = 'sign_language'
}

export type FeedbackReaction =
    | ''
    | 'clap' // 👏
    | 'thumbup' // 👍
    | 'dislike' // 👎
    | 'like' // ❤️
    | 'laughing' // 😂
    | 'shocked' // 😲
    | 'eyeroll' // 🙄
    | 'celebrate' // 🎉
    | 'yes_mark' // ✅
    | 'no_mark' // ❌
    | 'slower' // ⏪
    | 'faster' // ⏩
    | 'coffee' // ☕
    | 'question'; // ❓

export enum FrontOfClassType {
    None = 'None',
    InstructorAssistant = 'TAs',
    Presenters = 'Presenting Group',
    Speakers = 'Speaking',
    InstructorCamera = 'Instructor Camera',
    ClassCamera = 'Class Camera',
    SignLanguageCamera = 'Sign Language Camera'
}

export enum SeatingChart {
    Off = 'off',
    Default = 'default',
    FirstName = 'firstName',
    LastName = 'lastName',
    HandRaised = 'handRaised',
    ActiveSpeakers = 'activeSpeakers',
    Actions = 'actions',
    Unverified = 'unverified',
    Participation = 'participation'
}

export type ChatPermission = 'noone' | 'instructor-only' | 'everyone-directly' | 'everyone-publicly';

export type PrivacyMode = 'on' | 'off' | 'optional';

export type LmsToolCategory =
    | 'assignment'
    | 'quiz'
    | 'module'
    | 'page'
    | 'file'
    | 'forum'
    | 'announcement'
    | 'discussion'
    | 'assessment'
    | 'resource'
    | 'rubric'
    | 'column'
    | 'link'
    | 'journal'
    | 'lesson'
    | 'activity'
    | 'topic'
    | 'self_assessment'
    | 'checklist'
    | 'survey'
    | 'chat'
    | 'lti'
    | 'questionnaire'
    | 'workshop'
    | 'folder'
    | 'choice'
    | 'assign'
    | 'label'
    | 'wiki'
    | 'book'
    | 'url'
    | 'unknown_activity'
    | 'discussion_topic'
    | 'discussion_forum'
    | 'blog'
    | 'image'
    | 'video';

export type ExternalContentRaw = {
    sid: number;
    class_id: string;
    id: string;
    title: string;
    category: LmsToolCategory;
    url?: string;
    module_id: string | null;
    module_title: string | null;
    html: string | null;
    data?: any;
};

export type MeetingStateRaw = {
    meeting: Meeting;
    class: Class;
    class_session: Session;
    zoomstates: ClassAttributesRaw[];
    students: Student[];
    external_content?: ExternalContentRaw[];
    tabs: Tab[];
    school: SchoolSettingsRaw;
    user_settings: unknown[];
    cross_user_settings: unknown[];
    chats: ChatMessage[];
    content_counts: ContentCount[];
    cms_settings?: CmsSettings;
    pinned_tab: PinnedTab;
    polls: Array<PollLaunchedRecord>;
};

export type ClassAttributesRaw = {
    apptype?: string;
    breakoutId?: string | number;
    breakoutIdUtime?: number;
    breakoutIDString?: string;
    device: DeviceInfo;
    // Shared with instructors/presenters
    feedback?: FeedbackReaction;
    feedbackTime?: number;
    feedbackUtime?: number;
    focused?: boolean | 'true' | 'false';
    focusedTime?: number;
    focusedUtime?: number;
    isAudioMuted?: boolean;
    isAudioMutedUtime?: number;
    isHost: boolean;
    isRecording?: boolean;
    isTalking?: boolean;
    isTalkingUtime?: number;
    isVideoOn?: boolean;
    isVideoOnUtime?: number;
    joinTime: number;
    participant_id: string;
    persistent_id: string;
    persistent_session_id: string;
    presenting?: boolean | 'true' | 'false';
    presentingUtime?: number;
    raisedHand?: boolean | number;
    raisedHandTime?: number;
    raisedHandUtime?: number;
    // Shared with everyone
    reaction?: FeedbackReaction;
    reactionTime?: number;
    reactionUtime?: number;
    spotlight?: boolean;
    spotlightUtime?: number;
    stars?: number;
    starsUtime?: number;
    studentData?: {
        email: string;
        firstName: string;
        lastName: string;
        randomValue: number;
        role: ClassRole;
        sid: number;
        studentID: number;
        studentUUID: string;
        user_image: null | string;
        user_name: string;
    };
    studentID: number;
    studentIDUtime: number;
    talkTime?: number;
    tempRole?: TemporaryRole;
    tempRoleUtime?: number;
    userName: string;
    userNameUtime: number;
    visibility_mode?: 'normal' | 'hidden';
    zoomId: string;
    zoomIdUtime: number;
    zoomIDUtime?: number;
    zoom_idUtime?: number;
    privateDiscussion?: {
        isActive?: boolean;
        hideVideoInMeeting?: boolean;
    };
};

export type MeetingState = CamelCased<MeetingStateRaw>;
export type ClassAttributes = CamelCased<ClassAttributesRaw>;
export type StateData = CamelCased<StateDataRaw>;
export type SettingsData = CamelCased<SettingsDataRaw>;
export type ExternalContent = CamelCased<ExternalContentRaw>;
